import type { components } from '@/api/schema';

import { keysOf, labelOf, optionsOf } from './humanize';

type Schemas = components['schemas'];

export type PackageStatus = Schemas['JobPackageView']['status'];
export type PackageItemStatus = Schemas['JobPackageItem']['status'];
export type PackageCategory = Schemas['JobPackageItem']['category'];
export type PackageTaskStatus = Schemas['JobPackageTask']['status'];
export type FollowUpKind = Schemas['FollowUpTaskView']['kind'];
export type FollowUpStatus = Schemas['FollowUpTaskView']['status'];
export type FollowUpCategory = Schemas['FollowUpComplete']['category'];
export type FollowUpDraftStatus = Schemas['FollowUpTaskView']['draftStatus'];
export type ReplacementProvenance = Schemas['ReplacementLineView']['provenance'];
export type ReplacementChoice = Schemas['ReplacementDisposition']['choice'];

/** Where a replacement handoff, worksheet or review task stands; the schema leaves each open. */
export type ReplacementHandoffStatus = 'QUEUED' | 'ROUTED';
export type WorksheetStatus = 'DRAFT' | 'APPROVED';
export type ReviewTaskStatus = 'OPEN' | 'RESOLVED';

/** What a line of a package's approval trail records; the schema leaves it a plain string. */
export type PackageTrailAction =
  | 'ASSEMBLED'
  | 'ITEM_PENDING'
  | 'ITEM_CONFIRMED'
  | 'ITEM_NOT_REQUIRED'
  | 'ITEM_BLOCKED'
  | 'WINDOW_CHOSEN'
  | 'OVERRIDDEN'
  | 'SCHEDULED';

export const PACKAGE_STATUS_LABELS: Record<PackageStatus, string> = {
  DRAFT: 'In assembly',
  READY: 'Ready',
  SCHEDULED: 'Scheduled',
  CANCELLED: 'Cancelled',
};

export const PACKAGE_ITEM_STATUS_LABELS: Record<PackageItemStatus, string> = {
  PENDING: 'Not yet confirmed',
  CONFIRMED: 'Confirmed',
  NOT_REQUIRED: 'Not required',
  BLOCKED: 'Blocked',
};

export const PACKAGE_CATEGORY_LABELS: Record<PackageCategory, string> = {
  EQUIPMENT: 'Equipment',
  ACCESSORY: 'Labour and materials',
  PERMIT: 'Permit',
  FINANCING: 'Financing',
  WARRANTY: 'Warranty',
  PAYMENT: 'Payment',
  SCHEDULE: 'Schedule',
  SITE_NOTE: 'Site notes',
};

export const PACKAGE_TASK_STATUS_LABELS: Record<PackageTaskStatus, string> = {
  OPEN: 'Open',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};

export const PACKAGE_TRAIL_LABELS: Record<PackageTrailAction, string> = {
  ASSEMBLED: 'Package assembled',
  ITEM_PENDING: 'Line reopened',
  ITEM_CONFIRMED: 'Line confirmed',
  ITEM_NOT_REQUIRED: 'Line marked not required',
  ITEM_BLOCKED: 'Line blocked',
  WINDOW_CHOSEN: 'Installation window chosen',
  OVERRIDDEN: 'Open checks overridden by a manager',
  SCHEDULED: 'Installation scheduled',
};

export const FOLLOW_UP_KIND_LABELS: Record<FollowUpKind, string> = {
  SATISFACTION_WEEK_1: 'Satisfaction check · one week',
  SATISFACTION_MONTH_1: 'Satisfaction check · one month',
  CALLBACK: 'Callback',
  INSTALL_PROBLEM: 'Reported problem',
};

export const FOLLOW_UP_STATUS_LABELS: Record<FollowUpStatus, string> = {
  OPEN: 'Open',
  DONE: 'Done',
  ESCALATED: 'Escalated',
  CANCELLED: 'Cancelled',
};

export const FOLLOW_UP_CATEGORY_LABELS: Record<FollowUpCategory, string> = {
  SERVICE: 'Service',
  SALES: 'Sales',
  BILLING: 'Billing',
  WARRANTY: 'Warranty',
};

export const FOLLOW_UP_DRAFT_LABELS: Record<FollowUpDraftStatus, string> = {
  NONE: 'No message drafted',
  DRAFT: 'Draft waiting for approval',
  APPROVED: 'Approved to send',
};

export const REPLACEMENT_HANDOFF_STATUS_LABELS: Record<ReplacementHandoffStatus, string> = {
  QUEUED: 'Waiting to be routed',
  ROUTED: 'Routed',
};

export const WORKSHEET_STATUS_LABELS: Record<WorksheetStatus, string> = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
};

export const REVIEW_TASK_STATUS_LABELS: Record<ReviewTaskStatus, string> = {
  OPEN: 'Open',
  RESOLVED: 'Resolved',
};

// A worksheet figure is either a price somebody quoted or a number a person put there; the
// second says so, because the worksheet is only as good as the people who supplied it.
export const REPLACEMENT_PROVENANCE_LABELS: Record<ReplacementProvenance, string> = {
  ACTUAL_PRICE: 'Actual price',
  ESTIMATE: 'Human estimate',
};

export const REPLACEMENT_CHOICE_LABELS: Record<ReplacementChoice, string> = {
  REPAIR: 'Repair',
  REPLACE: 'Replace',
  WAIT: 'Wait',
  DISAGREEMENT: 'Disagreement or complaint',
};

/** The four headings a follow-up may be filed under. */
export const FOLLOW_UP_CATEGORIES = keysOf(FOLLOW_UP_CATEGORY_LABELS);

/** The provenances a worksheet figure may carry, as `{ value, label }`, for a select. */
export const REPLACEMENT_PROVENANCE_OPTIONS = optionsOf(REPLACEMENT_PROVENANCE_LABELS);

export function packageStatusLabel(status: string | null | undefined): string {
  return labelOf(PACKAGE_STATUS_LABELS, status);
}

export function packageItemStatusLabel(status: string | null | undefined): string {
  return labelOf(PACKAGE_ITEM_STATUS_LABELS, status);
}

export function packageCategoryLabel(category: string | null | undefined): string {
  return labelOf(PACKAGE_CATEGORY_LABELS, category);
}

export function packageTaskStatusLabel(status: string | null | undefined): string {
  return labelOf(PACKAGE_TASK_STATUS_LABELS, status);
}

export function packageTrailLabel(action: string | null | undefined): string {
  return labelOf(PACKAGE_TRAIL_LABELS, action);
}

export function followUpKindLabel(kind: string | null | undefined): string {
  return labelOf(FOLLOW_UP_KIND_LABELS, kind);
}

export function followUpStatusLabel(status: string | null | undefined): string {
  return labelOf(FOLLOW_UP_STATUS_LABELS, status);
}

export function followUpCategoryLabel(category: string | null | undefined): string {
  return labelOf(FOLLOW_UP_CATEGORY_LABELS, category);
}

export function followUpDraftLabel(status: string | null | undefined): string {
  return labelOf(FOLLOW_UP_DRAFT_LABELS, status);
}

export function replacementHandoffStatusLabel(status: string | null | undefined): string {
  return labelOf(REPLACEMENT_HANDOFF_STATUS_LABELS, status);
}

export function worksheetStatusLabel(status: string | null | undefined): string {
  return labelOf(WORKSHEET_STATUS_LABELS, status);
}

export function reviewTaskStatusLabel(status: string | null | undefined): string {
  return labelOf(REVIEW_TASK_STATUS_LABELS, status);
}

export function replacementProvenanceLabel(provenance: string | null | undefined): string {
  return labelOf(REPLACEMENT_PROVENANCE_LABELS, provenance);
}

export function replacementChoiceLabel(choice: string | null | undefined): string {
  return labelOf(REPLACEMENT_CHOICE_LABELS, choice);
}
